import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const BANKS = ["ING", "Deutsche Bank", "Postbank", "Commerzbank", "DZ BANK"];

function Fieldset({ title, children }) {
  return (
    <div className="sm:col-span-2">
      <p className="tech-label mb-3 mt-2 text-accent">{title}</p>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}
function F({ id, label, value, onChange, type = "text", required, placeholder }) {
  return (<div className="flex flex-col gap-2"><Label htmlFor={id}>{label}</Label><Input id={id} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} /></div>);
}

export function CreateClientForm({ onDone }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", institutionName: "", institutionType: "bank", accountHolder: "", iban: "", bic: "", bankName: "Deutsche Bank", currency: "EUR", idFullName: "", idType: "id", idNumber: "", birthDate: "", nationality: "" });
  const u = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError(null);
    try { await api.post("/admin/clients", f); onDone(); }
    catch (err) { setError(err?.response?.data?.detail ?? "Fehler"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="premium-card p-6">
      <h2 className="font-serif text-lg font-semibold text-foreground">{t("admin.form.title")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("admin.form.subtitle")}</p>
      <form onSubmit={submit} className="mt-5 grid gap-4 sm:grid-cols-2">
        <Fieldset title={t("admin.form.section.access")}>
          <F id="name" label={t("admin.form.name")} value={f.name} onChange={u("name")} required />
          <div className="flex flex-col gap-2"><Label>{t("admin.form.type")}</Label>
            <Select value={f.institutionType} onValueChange={(v) => setF({ ...f, institutionType: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="bank">{t("auth.type.bank")}</SelectItem><SelectItem value="asset">{t("auth.type.asset")}</SelectItem><SelectItem value="fund">{t("auth.type.fund")}</SelectItem><SelectItem value="other">{t("auth.type.other")}</SelectItem></SelectContent>
            </Select>
          </div>
          <F id="institution" label={t("admin.form.institution")} value={f.institutionName} onChange={u("institutionName")} required />
          <F id="email" label={t("admin.form.email")} type="email" value={f.email} onChange={u("email")} required />
          <F id="password" label={t("admin.form.password")} type="password" value={f.password} onChange={u("password")} required />
        </Fieldset>
        <Fieldset title={t("admin.form.section.bank")}>
          <F id="holder" label={t("admin.form.accountHolder")} value={f.accountHolder} onChange={u("accountHolder")} required />
          <div className="flex flex-col gap-2"><Label>{t("admin.form.bankName")}</Label>
            <Select value={f.bankName} onValueChange={(v) => setF({ ...f, bankName: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{BANKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <F id="iban" label={t("admin.form.iban")} value={f.iban} onChange={u("iban")} required placeholder="DE00 ..." />
          <F id="bic" label={t("admin.form.bic")} value={f.bic} onChange={u("bic")} />
          <div className="flex flex-col gap-2"><Label>{t("admin.form.currency")}</Label>
            <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["EUR", "USD", "CHF", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </Fieldset>
        <Fieldset title={t("admin.form.section.identity")}>
          <F id="idname" label={t("admin.form.idFullName")} value={f.idFullName} onChange={u("idFullName")} required />
          <div className="flex flex-col gap-2"><Label>{t("admin.form.idType")}</Label>
            <Select value={f.idType} onValueChange={(v) => setF({ ...f, idType: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="id">{t("admin.idType.id")}</SelectItem><SelectItem value="passport">{t("admin.idType.passport")}</SelectItem><SelectItem value="residence">{t("admin.idType.residence")}</SelectItem></SelectContent>
            </Select>
          </div>
          <F id="idnum" label={t("admin.form.idNumber")} value={f.idNumber} onChange={u("idNumber")} required />
          <F id="bdate" label={t("admin.form.birthDate")} value={f.birthDate} onChange={u("birthDate")} placeholder="TT.MM.JJJJ" required />
          <F id="nat" label={t("admin.form.nationality")} value={f.nationality} onChange={u("nationality")} required />
        </Fieldset>
        {error && <p className="text-sm text-destructive sm:col-span-2">{error}</p>}
        <div className="flex gap-3 sm:col-span-2">
          <Button type="submit" disabled={busy}>{busy ? t("auth.wait") : t("admin.form.create")}</Button>
          <Button type="button" variant="outline" onClick={onDone}>{t("admin.form.cancel")}</Button>
        </div>
      </form>
    </Card>
  );
}
